/**
 * Схемы базы знаний (Б1): фронтматтер файла инструкции и раздел `knowledge:` конфига бренда.
 * Норма — docs/phase-2/02-knowledge.md: §4.1 (фронтматтер), §3.2 (id = путь) и §5.5
 * (KnowledgeRefsSchema).
 *
 * Живут здесь, а не в `@smm/knowledge`, потому что zod объявлен ровно у `@smm/contracts`
 * (правило R1, §Окружение 2) — тот же переезд, который уже проделала `BrandConfigSchema`.
 * Сам разбор файлов (YAML, файловая система, нормализация тел) — в `@smm/knowledge`.
 */

import * as z from 'zod';
import { BRAND_RE } from '../approval-key/limits.ts';
import { limitValue, TEXT_MAX_CODEPOINTS } from '../units.ts';

/**
 * id инструкции = путь от `knowledge/` без расширения (§3.2): минимум один слэш — файл
 * всегда лежит в каталоге, не в корне; точка допустима только в последнем сегменте
 * (`core/system.writer`). Коллизия id невозможна по построению: id и есть путь.
 */
export const KNOWLEDGE_ID_RE = /^[a-z0-9][a-z0-9-]*(?:\/[a-z0-9][a-z0-9-]*)*\/[a-z0-9][a-z0-9.-]*$/;

/**
 * Ссылка на инструкцию из конфига бренда — форма §5.5 дословно. Шире KNOWLEDGE_ID_RE
 * (точка разрешена в любом не-первом сегменте); ссылку, которой не соответствует ни один
 * файл, отбивает загрузчик при старте, а не схема — тот же приём, что у пустого channel_id.
 */
export const KNOWLEDGE_REF_RE = /^[a-z0-9][a-z0-9-]*(\/[a-z0-9][a-z0-9.-]*)+$/;

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * id формата — сегмент после `formats/`: он уходит в `IntakeDraftBody.format` дословно и
 * дальше в SQL, в фильтры UI и в метрики (02-knowledge.md §8.2), поэтому латиница и дефис.
 */
export const KNOWLEDGE_FORMAT_ID_RE = /^[a-z0-9][a-z0-9-]*$/;

const FORMAT_ID_PREFIX = 'formats/';

/**
 * Блок `format:` файла инструкции — таблица 03-generation.md §5.1 (`kind: format`) поле в
 * поле, плюс `order`. Присутствует ТОЛЬКО у файлов `knowledge/formats/<id>.md`.
 *
 * Смысл блока: определение формата — данные, а не код (02-knowledge.md §3.4). До него
 * коридор длины жил литералом в `services/agent-writer/src/pipeline/format.ts`, то есть
 * редакторское решение PR lead требовало правки TypeScript. Добавить формат теперь — это
 * новый файл; перенастроить коридор — правка одной строки YAML.
 *
 * ЧЕГО ЗДЕСЬ БОЛЬШЕ НЕТ и почему. `method` ушёл: способ отправки — свойство поста, и его
 * выводит `wireMethodOf` по медиа и длине канонического текста, а не редактор в YAML.
 * `caption_above_media` ушёл: поле осталось внутри подписываемых байтов, но стало инертным —
 * картинка всегда первым блоком. `degrade_to` ушёл: деградация существовала потому, что
 * картинка съедала три четверти поста подписью в 1024 знака; теперь не съедает.
 * Схема СТРОГАЯ намеренно: файл, не прошедший миграцию, обязан уронить старт с именем файла,
 * а не тихо потерять три строки.
 *
 * `order` — тот же класс решения: `catalog.ids()` уходит в задачу выбора формата дословно
 * (`choiceTask`), то есть порядок предъявления форматов модели ВИДЕН В ПРОМПТЕ. Пока он
 * жил массивом `WRITER_FORMAT_IDS`, добавление формата было правкой кода даже при готовом
 * файле. Порядок в списке конфига бренда для этого не годится: он задаёт порядок БЛОКОВ
 * промпта (§5.1) и существует у бренда, а библиотека форматов — общая (`brand: null`).
 */
export const KnowledgeFormatSchema = z
  .strictObject({
    /** Порядок предъявления модели; уникален в пределах библиотеки — проверяет загрузчик. */
    order: z.int().min(1),
    budget_codepoints: z.strictObject({
      /** Редакторский коридор `[min, max]` в КОДПОИНТАХ (§6.2), не потолок. */
      target: z.tuple([z.int().min(1), z.int().min(1)]),
      /** Собственный жёсткий потолок формата; выше телеграмного быть не может. */
      hard: z.int().min(1),
    }),
    /** Р10: `off` → `is_disabled:true`; `card` → крупная карточка, адрес — первая ссылка текста. */
    link_preview: z.enum(['off', 'card']),
    /**
     * Первая строка поста — лид, и он весь жирный МЕХАНИЧЕСКИ: сборка черновика накрывает
     * первый блок одной сущностью bold, что бы ни написала модель (formats/news НОВ-5,
     * решение Ильи 2026-09-02: модель делала лид жирным нестабильно). Ссылки внутри лида
     * остаются. Умолчание — false: у остальных форматов лида нет.
     */
    lead_bold: z.boolean().default(false),
    /**
     * Ссылка на первоисточник в посте. `footer` — сборка черновика САМА дописывает её
     * последней строкой; `off` — формат ставит ссылки сам по своим правилам (сводка
     * недели: ссылка в каждой строке); `inline` — ссылка стоит ВНУТРИ текста, на глаголе
     * действующего лица в первой фразе новости (AGI Boardroom, решение Ильи 2026-11-02).
     * Адрес первоисточника модель не пишет ни при каком значении.
     *
     * Решение Ильи 2026-09-08, и это ровно тот же ход, что уже сделан с жирным лидом
     * (`lead_bold` выше, `services/agent-writer/src/pipeline/build.ts:132-134`): правило
     * НОВ-5 требовало ссылку от модели, а НЕЙ-11 `[обязательное]` запрещало markdown-ссылки
     * в посте и грузилось РАНЬШЕ — модель писала ссылку нестабильно, чаще не писала вовсе.
     * Механическая дописка снимает и противоречие базы знаний, и недетерминированность.
     *
     * `inline` этого решения не отменяет, а делит работу по-другому: модель выбирает только
     * СЛОВО — пишет ровно одну ссылку диалекта на адрес-заглушку `SOURCE_LINK_PLACEHOLDER_URL`
     * (`telegram/draft.ts`), — а адрес ставит сборка из материалов темы, и только адрес
     * официального первоисточника; нет его — нет и ссылки. Глагол — решение языка, а не
     * механики, и угадывать его кодом значило бы чинить русскую морфологию регулярками; адрес
     * — наоборот, решение данных, и модели, читавшей открытый интернет, его не доверяют.
     *
     * Умолчание `off`: схема СТРОГАЯ, и поле обязано быть объявлено здесь ДО того, как оно
     * появится во фронтматтере хоть одного файла, — иначе загрузка формата падает с
     * «неизвестный ключ». Умолчание же означает «форматы, которых правка не касается, не
     * меняются ни на байт».
     */
    source_link: z.enum(['off', 'footer', 'inline']).default('off'),
    /**
     * Неизменная первая строка поста, которую сборка черновика вставляет САМА, жирным, над
     * текстом модели (дайджест недели, релиз 2026-10-14: «Дайджест недели — главное, что стоит
     * знать из мира SEO/GEO»). Модель её не пишет, гейт живого текста её не видит
     * (`authoredText`), и тире в ней не считается нормой тире канала — заголовок не авторский.
     *
     * Ход тот же, что у `lead_bold` и `source_link`: форма поста — решение формата, а не вкус
     * модели, и модель, которой велели «первой строкой поставь вот это», переписывала бы
     * заголовок под нормы знаков (тире 0, ФИЛ-8).
     *
     * НЕОБЯЗАТЕЛЬНОЕ и без умолчания: формат без поля не меняется ни на байт, а копии стиля,
     * снятые до релиза, грузятся как раньше. Одна строка без переводов строк и без разметки
     * диалекта: звёздочки или скобки здесь стали бы символами в канале.
     */
    title_line: z
      .string()
      .min(1)
      .max(200)
      .regex(/^[^\r\n*_[\]`~|]+$/u, 'одна строка текста без переводов строк и без разметки')
      .refine((s) => s.trim() === s, 'без краевых пробелов')
      .optional(),
    /**
     * Пункты поста нумеруются `N. ` (дайджест недели v4, docs/release-2026-10-16-digest-v4.md).
     * Нумерует модель, сверяет писатель (`checkDigest`). Ключ — ещё и метка поколения формата:
     * дайджест без него (v3 и старше в копиях стиля) писатель отклоняет ДО платного вызова, как
     * дайджест без `title_line`. Отсутствие — пункты не нумеруются.
     */
    numbered_items: z.boolean().optional(),
    /**
     * Потолок выдержки материала в брифе писателя для ЭТОГО формата, кодпоинты, — поверх
     * `generation.material_excerpt_max` бренда (решение 2026-09-21, `api/brand-config.ts`).
     * Задан — побеждает бренд для своего формата; нет — действует потолок бренда, как раньше.
     *
     * Зачем свой. Потолок бренда подобран под новость из одного материала: 600 кодпоинтов
     * лида и сути хватает, а 3,6–6 тыс. знаков выдержки ловили отказы `reasoning_extraction`.
     * Обзор конференции (`event-review`) собирается из нескольких материалов, и каждому из них
     * нужно больше, чем лид, — иначе обзор пересказывает заголовки. Один потолок на бренд
     * заставил бы выбирать между отказами новостей и пустыми обзорами.
     *
     * Диапазон тот же, что у потолка бренда (200..6000): выше 6000 выдержки не бывает
     * (`EVIDENCE_EXCERPT_MAX`), ниже 200 — уже не выдержка. Необязателен и без умолчания:
     * формат без поля и копии стиля, снятые до релиза, не меняются ни на байт.
     */
    excerpt_max: z.int().min(200).max(6000).optional(),
    /** Окно свежести формата в СУТКАХ; `null` — вечнозелёный (01-monitoring §10.3). */
    freshness_days: z.union([z.int().min(1), z.null()]),
  })
  .check((ctx) => {
    const v = ctx.value;
    const [lo, hi] = v.budget_codepoints.target;
    if (hi < lo) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['budget_codepoints', 'target'],
        message: `коридор ${String(lo)}–${String(hi)} бессмыслен: верх ниже низа`,
      });
    }
    if (v.budget_codepoints.hard < hi) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['budget_codepoints', 'hard'],
        message: `верх коридора ${String(hi)} выше собственного потолка ${String(v.budget_codepoints.hard)}`,
      });
    }
    // Отказ загрузки, а не обрезка (§5.1): формат с потолком выше телеграмного — это пост,
    // который валидатор выбросит уже после того, как за него заплачено.
    // Потолок ОДИН на все форматы. Второго больше нет: подписи к картинке не существует,
    // а способ отправки — свойство ПОСТА, а не формата (09-rich-transport.md §1.2ж).
    // Формат объявляет, сколько текста он себе позволяет, и не знает, чем пост уедет.
    const ceiling = limitValue(TEXT_MAX_CODEPOINTS);
    if (v.budget_codepoints.hard > ceiling) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['budget_codepoints', 'hard'],
        message:
          `потолок ${String(v.budget_codepoints.hard)} выше предела Telegram ${String(ceiling)} ` +
          '— правьте формат, обрезать текст на лету нельзя (§5.1)',
      });
    }
  });

export type KnowledgeFormat = z.output<typeof KnowledgeFormatSchema>;

/**
 * Фронтматтер файла инструкции — таблица §4.1 поле в поле. В промпт он не попадает никогда
 * (§4.2: туда идёт только тело), поэтому правка `updated:` или `owner:` не меняет
 * `body_sha256` и не требует бампа `version`.
 */
/**
 * Режимы загрузки базы (решение Ильи 2026-09-14). Файл едет в промпт только в режимах, которые
 * перечислены в его `modes`:
 *   · `post` — обычный пост: голос, фильтр, разметка, форматы кроме сводки недели;
 *   · `weekly-digest` — сводка недели: те же файлы плюс `formats/weekly-digest`;
 *   · `visual` — вердикт картинки по кнопке: ядро слоя A плюс `publishing/visuals`;
 *   · `poster` — дизайнер обложки (docs/phase-2/10-posters.md): охранные правила слоя A плюс
 *     `publishing/poster`. Роль писателя (`core/system.writer`) сюда не едет намеренно: вызов
 *     не пишет пост, и «ты пишешь пост» в его промпте было бы неправдой о задании;
 *   · `event-review` — обзор конференции (AGI Boardroom, 2026-11-02): файлы без поля `modes`,
 *     файлы, чей явный список называет режим (у `core/system.writer` и `core/guardrails` список
 *     явный — без `event-review` в нём роль и охранные правила в обзор не поедут), и
 *     `formats/event-review`. Свой режим, а не `post`, ради двух свойств сразу: формат обзора
 *     никогда не предлагается модели в выборе формата (выбирают только форматы режима `post`),
 *     и его правила не едут в промпт каждой новости бренда.
 * Без поля файл считается обычным (`KNOWLEDGE_DEFAULT_MODES`): так ни один существующий файл не
 * меняет поведения, а правила картинки и сводки недели перестают ехать в каждый вызов письма.
 */
export const KNOWLEDGE_MODES = [
  'post',
  'weekly-digest',
  'visual',
  'poster',
  'event-review',
] as const;
export type KnowledgeMode = (typeof KNOWLEDGE_MODES)[number];

/**
 * Код формата обзора конференции — он же его режим загрузки базы. Одна строка на всех, кто её
 * знает: мониторинг ставит её темой (`format_hint`), бэкенд по ней ищет выпуск в `event_watch`
 * бренда, писатель выбирает режим промпта, интерфейс подписывает кнопку. Разойдясь на букву,
 * они дали бы тему, которую писатель пишет новостью.
 */
export const EVENT_REVIEW_FORMAT = 'event-review';

/**
 * Режимы файла без поля `modes`: обычный пост, сводка недели и обзор конференции, но не картинка
 * и не обложка. Список ОДИН — его берут и умолчание схемы ниже, и снапшоты, собранные литералом
 * (`DEFAULT_KNOWLEDGE_MODES` в `@smm/knowledge`): два списка разошлись бы на первом же новом
 * режиме, и общий файл голоса молча выпал бы из промпта нового формата.
 */
export const KNOWLEDGE_DEFAULT_MODES: readonly KnowledgeMode[] = [
  'post',
  'weekly-digest',
  EVENT_REVIEW_FORMAT,
];

export const KnowledgeFrontmatterSchema = z
  .strictObject({
    /** Обязан совпадать с фактическим путём файла — проверяет загрузчик, не схема. */
    id: z.string().regex(KNOWLEDGE_ID_RE, 'ожидался id-путь вида publishing/telegram (§3.2)'),
    /** Для логов и человека; в промпт не идёт. */
    title: z.string().min(1).max(80),
    /** Растёт на 1 при любой правке тела; за дисциплиной следит knowledge.lock.json (§3.3). */
    version: z.int().min(1),
    /** Слой промпта (§5.1). `C` и `D` файлами не бывают — они считаются из БД и задания. */
    layer: z.enum(['A', 'B']),
    /** Кто вообще имеет право подключить инструкцию; непустой. */
    applies_to: z.array(z.enum(['writer', 'monitor'])).min(1),
    /** `null` — общая для всех брендов; явный код — только его конфиг может её подключить. */
    brand: z.union([z.string().regex(BRAND_RE), z.null()]),
    status: z.enum(['draft', 'active', 'deprecated']),
    /** Человек, а не роль: кому писать вопрос по содержанию. */
    owner: z.string().min(1),
    /** Откуда взято: `docs/telegram.md §4`, `05_tone_of_voice §6`, … */
    derived_from: z.array(z.string().min(1)).optional(),
    updated: z.string().regex(DATE_RE, 'ожидалось YYYY-MM-DD'),
    /** В каких режимах загрузки файл попадает в промпт (см. KNOWLEDGE_MODES). */
    modes: z
      .array(z.enum(KNOWLEDGE_MODES))
      .min(1)
      .default([...KNOWLEDGE_DEFAULT_MODES]),
    /** Дата, после которой `knowledge:check` предупреждает «пора перечитать». */
    review_by: z.string().regex(DATE_RE, 'ожидалось YYYY-MM-DD').optional(),
    /**
     * Определение формата — НЕОБЯЗАТЕЛЬНО: девять полей выше остаются полным контрактом
     * любой инструкции, и ни один существующий файл от появления блока не меняется.
     */
    format: KnowledgeFormatSchema.optional(),
  })
  .check((ctx) => {
    const v = ctx.value;
    if (v.format === undefined) return;
    // Блок формата на файле вне `formats/` — опечатка, и молча она стоит того, что писатель
    // не найдёт формат по id, который обещает имя файла.
    const suffix = v.id.startsWith(FORMAT_ID_PREFIX) ? v.id.slice(FORMAT_ID_PREFIX.length) : null;
    if (suffix === null || !KNOWLEDGE_FORMAT_ID_RE.test(suffix)) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['format'],
        message: `блок format законен только у файла knowledge/formats/<id>.md, а id — «${v.id}»`,
      });
    }
    if (v.layer !== 'B') {
      // Слой A — общий системный префикс всех брендов (§5.2). Библиотека форматов в нём
      // означала бы, что состав форматов одного бренда переписывает кэш другого.
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['layer'],
        message: `формат живёт в слое B, а не ${v.layer} (§5.1)`,
      });
    }
  });

export type KnowledgeFrontmatter = z.output<typeof KnowledgeFrontmatterSchema>;

/**
 * Раздел `knowledge:` конфига бренда (§5.5). Порядок в списке — это порядок блоков в
 * промпте: вставка в середину — осознанное действие с видимой ценой (одна перезапись
 * кэша), а не побочный эффект имени файла (§5.1).
 *
 * Списка ровно два, по числу сервисов, которые читают базу знаний, — и это же значения
 * `applies_to` фронтматтера. Третьего среза нет: у каждого сервиса один состав инструкций,
 * а «облегчённый набор для другого способа запуска» означал бы, что в канал уходят посты,
 * написанные по другой базе, чем та, которую вычитал владелец.
 */
export const KnowledgeRefsSchema = z
  .strictObject({
    writer: z.array(z.string().regex(KNOWLEDGE_REF_RE)).default([]),
    monitor: z.array(z.string().regex(KNOWLEDGE_REF_RE)).default([]),
  })
  .check((ctx) => {
    // Дубль в списке — это второй экземпляр той же инструкции в промпте: модель получает
    // противоречивый сигнал «это важно дважды», а мы платим за токены дважды.
    for (const key of ['writer', 'monitor'] as const) {
      const seen = new Map<string, number>();
      ctx.value[key].forEach((ref, i) => {
        const first = seen.get(ref);
        if (first === undefined) {
          seen.set(ref, i);
          return;
        }
        ctx.issues.push({
          code: 'custom',
          input: ctx.value,
          path: [key, i],
          message: `${ref} повторяет ${key}[${String(first)}]`,
        });
      });
    }
  });

export type KnowledgeRefs = z.output<typeof KnowledgeRefsSchema>;
